import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore, useSettingsStore } from '../store';
import { geoService } from '../services';
import { getSocket } from '../store/useNotificationStore';

/**
 * Geocerca: verifica una vez por carga de pagina desde donde se esta usando el sistema.
 *
 * Reglas de seguridad de este hook:
 *  - Con `mode: 'off'` NO se llama a navigator.geolocation, asi que el navegador
 *    ni siquiera muestra el dialogo de permiso. El modo apagado es realmente inerte.
 *  - El superadmin queda exento (viaja entre talleres por definicion).
 *  - Cualquier fallo (sin red, backend caido, permiso denegado en modo no bloqueante)
 *    deja pasar al usuario. Solo el modo `block` deniega, y esa decision la toma
 *    el backend, nunca este codigo.
 */

const GEO_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 5 * 60 * 1000, // acepta una lectura de hasta 5 min: evita repreguntar al GPS
};

/** Envuelve la API del navegador en una promesa que nunca rechaza. */
export const readPosition = () =>
  new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ status: 'unavailable' });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        }),
      (error) =>
        resolve({
          // 1 = PERMISSION_DENIED · 2 = POSITION_UNAVAILABLE · 3 = TIMEOUT
          status: error?.code === 1 ? 'denied' : 'unavailable',
        }),
      GEO_OPTIONS
    );
  });

/**
 * Caja de traspaso entre el login y el Layout.
 *
 * El permiso de ubicacion se pide en la pantalla de login (asi un logout/login
 * obliga a pasar por el), pero la lectura se REGISTRA desde el Layout, cuando ya
 * hay socket y sesion abierta contra la cual guardarla. Para no mostrarle al
 * usuario dos dialogos seguidos, el login deja aqui lo que obtuvo.
 */
const HANDOFF_KEY = 'geofence_login_reading';
const HANDOFF_MAX_AGE_MS = 2 * 60 * 1000;

export const storeLoginReading = (reading) => {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ ...reading, at: Date.now() }));
  } catch {
    // sessionStorage lleno o bloqueado: se volvera a pedir en el Layout
  }
};

const takeLoginReading = () => {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(HANDOFF_KEY); // de un solo uso
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > HANDOFF_MAX_AGE_MS) return null;
    delete parsed.at;
    return parsed;
  } catch {
    return null;
  }
};

export const useGeoFence = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { settings } = useSettingsStore();
  const [verdict, setVerdict] = useState(null);
  const checkedRef = useRef(false);
  const runningRef = useRef(false);

  const mode = settings?.geoFence?.mode || 'off';
  const isExempt = !user || user.role === 'superadmin';

  /**
   * Vuelve a pedir la ubicacion y reevalua. Se usa en el arranque y en el boton
   * "Reintentar" de la alerta, para que no haga falta recargar la pagina despues
   * de habilitar el permiso en el navegador.
   */
  const runCheck = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      // Si el login ya obtuvo la ubicacion, se reutiliza: evita un segundo dialogo
      const reading = takeLoginReading() || (await readPosition());
      const response = await geoService.checkin({
        ...reading,
        socketId: getSocket()?.id || null,
      });
      setVerdict(response?.data || response);
    } catch {
      // Sin red o backend caido: nunca se le impide trabajar al usuario
    } finally {
      runningRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isExempt) return;
    // Modo apagado: no se pide ubicacion ni se muestra el dialogo del navegador
    if (mode === 'off') return;
    // Una sola verificacion por carga de pagina (Layout no se remonta al navegar)
    if (checkedRef.current) return;
    checkedRef.current = true;

    runCheck();
  }, [isAuthenticated, isExempt, mode, runCheck]);

  /** El usuario vio la alerta y decidio continuar (solo posible en modo `warn`). */
  const acknowledge = useCallback(async () => {
    setVerdict(null);
    try {
      await geoService.acknowledge(getSocket()?.id || null);
    } catch {
      // El registro del "continuar" es informativo: si falla, no pasa nada
    }
  }, []);

  return {
    verdict,
    retry: runCheck,
    // Solo se muestra la alerta cuando el backend lo indica
    showAlert: !!(verdict?.shouldWarn || verdict?.shouldBlock),
    isBlocked: !!verdict?.shouldBlock,
    acknowledge,
  };
};

export default useGeoFence;
